import { gpHome } from '../app';
import { CurrentUserService } from '../../app-common/services/current-user-service';
import { IAuthService } from '../services/auth-service';
import { gpHasFeature, isAiV2FeatureFlagEnabled } from '../../app-common/util/feature-flags';
import {
    AccountRefreshAllEvent,
    AccountPanelBusyEvent,
    AccountPanelBusyPayload,
    applyPanelBusy,
    anyPanelBusy,
} from '../services/account-refresh-contract';
import { AccountPrefetchServiceName } from '../services/account-data-cache';
import { IAccountPrefetchApi } from '../services/account-prefetch-service';
import { greetingNameFrom } from '#gp/app-common/util/greeting';

import type * as ng from 'angular';
import accountTemplate from '../views/account.html';

interface IAccountControllerScope extends ng.IScope {
    email: string;
    showLicenseInfo: boolean;

    changeEmailShowing: boolean;
    changeEmailNewAddress: string;
    changeEmailCurrentPassword: string;
    changeEmailInfoMessage: string;
    changeEmailErrorMessage: string;
    changeEmailSubmitRequest(): void;

    showSelfServiceLicenseManager: boolean;
    showAiCredits: boolean;

    /** The greeting-name override; blank means "derive from the signup name". */
    greetingName: string;
    /** What blank derives to, shown as the field's placeholder. */
    greetingDefault: string | null;
    greetingSaving: boolean;
    greetingInfoMessage: string;
    greetingErrorMessage: string;
    saveGreetingName(): void;

    /** True while any panel is fetching, so the header button can show it. */
    refreshingAll: boolean;
    refreshAll(): void;
}

export const AccountController_ServiceName = "gpHome_AccountController";
export const AccountController_Template = accountTemplate;

gpHome.controller(AccountController_ServiceName, ['$scope', '$location', '$timeout', '$q', '$rootScope', 'authService', CurrentUserService.serviceName, AccountPrefetchServiceName,
    function ($scope: IAccountControllerScope, $location: ng.ILocationService, $timeout: ng.ITimeoutService, $q: ng.IQService, $rootScope: ng.IRootScopeService, authService: IAuthService, accountsService: CurrentUserService, accountPrefetchService: IAccountPrefetchApi) {
        new AccountController($scope, $q, $location, $rootScope, authService, accountsService, accountPrefetchService)
    }]);

class AccountController {

    constructor(
        private $scope: IAccountControllerScope,
        private $q: ng.IQService, $location: ng.ILocationService,
        $rootScope: ng.IRootScopeService,
        private authService: IAuthService,
        private accountsService: CurrentUserService,
        accountPrefetchService: IAccountPrefetchApi) {

        $scope.showLicenseInfo = false;
        $scope.refreshingAll = false;

        // Panels report busy/idle as they fetch, so the "Refresh all" spinner runs for
        // exactly as long as work is in flight rather than for a guessed interval.
        let busyPanels: Record<string, boolean> = {};

        $scope.refreshAll = () => $rootScope.$broadcast(AccountRefreshAllEvent);

        $scope.$on(AccountPanelBusyEvent, (_event: ng.IAngularEvent, payload: AccountPanelBusyPayload) => {
            busyPanels = applyPanelBusy(busyPanels, payload);
            $scope.refreshingAll = anyPanelBusy(busyPanels);
        });

        $scope.showSelfServiceLicenseManager = gpHasFeature('selfServiceLicenseManager')
        $scope.showAiCredits = isAiV2FeatureFlagEnabled();

        authService.checkAuthorisationIfNeeded().then(authInfo => {
            $scope.email = authInfo.email;
        });

        // Whether the licence section appears at all. Read from the prefetched payload
        // where there is one, so the section is in its final state on the first frame
        // rather than appearing under the user a moment later (#1229). The refresh
        // behind it asks for the payment check the panel wants, so the two share one
        // request instead of this controller issuing an `info` call of its own.
        const prefetchedInfo = accountPrefetchService.peekSubscriptionInfo();
        if (prefetchedInfo) {
            $scope.showLicenseInfo = prefetchedInfo.showLicenseOptions;
        }
        accountPrefetchService.fetchSubscriptionInfo(true).then(info => {
            $scope.showLicenseInfo = info.showLicenseOptions;
        }).catch(() => { });

        $scope.greetingName = '';
        $scope.greetingDefault = null;
        $scope.greetingSaving = false;
        $scope.greetingInfoMessage = '';
        $scope.greetingErrorMessage = '';

        accountsService.getUserSettings().then(settings => {
            $scope.greetingName = typeof settings.greetingName === 'string' ? settings.greetingName : '';
        });
        accountsService.getCurrentUserInfo().then(info => {
            $scope.greetingDefault = greetingNameFrom(info.name);
        });

        $scope.saveGreetingName = () => {
            $scope.greetingInfoMessage = '';
            $scope.greetingErrorMessage = '';
            $scope.greetingSaving = true;
            // A cleared field posts '' deliberately: the settings merge reads
            // null as "leave what is stored alone", so the empty string is how
            // an override is cleared (the greeting then derives from the signup
            // name). The stamp clears with it, so no reader finds provenance
            // standing over an override that is no longer there. The AdminPortal
            // writes this same pair, and badges a user-set value before an admin
            // replaces it.
            const greetingName = ($scope.greetingName || '').trim();

            this.accountsService.saveUserSettings({
                greetingName: greetingName,
                greetingNameSetBy: greetingName ? 'user' : '',
            })
                .then(() => { $scope.greetingInfoMessage = 'Saved. You will see it next time you start a plan.'; })
                .catch(() => { $scope.greetingErrorMessage = 'Saving failed. Please try again.'; })
                .finally(() => { $scope.greetingSaving = false; });
        };


        $scope.changeEmailSubmitRequest = () => {

            $scope.changeEmailInfoMessage = "";
            $scope.changeEmailErrorMessage = "";

            if ($scope.changeEmailNewAddress === $scope.email) {
                $scope.changeEmailErrorMessage = "Please type a different email address.";

            } else if (!$scope.changeEmailNewAddress) {
                $scope.changeEmailErrorMessage = "Please type in an email address.";

            } else if (!$scope.changeEmailCurrentPassword) {
                $scope.changeEmailErrorMessage = "Please enter your current password.";

            } else {
                var req = this.accountsService.requestEmailChange($scope.email, $scope.changeEmailNewAddress, $scope.changeEmailCurrentPassword);

                req.then(msg => $scope.changeEmailInfoMessage = msg);
                req.catch(reason => $scope.changeEmailErrorMessage = reason);
                req.finally(() => $scope.changeEmailCurrentPassword = '');
            }
        }
    }
} 
